import Link from "next/link";
import {
  ComponentProps,
  ComponentPropsWithoutRef,
  FC,
  ReactNode,
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { cn } from "@src/lib/utils/cn";

export type SubheaderTab = {
  label: string;
  value: string;
  testId?: string;
};

type Props = {
  tabs: SubheaderTab[];
  value: string;
  onValueChange: (value: string) => void;
  ariaLabel?: string;
  className?: string;
};

type SubheaderTabButtonProps = ComponentPropsWithoutRef<"button"> & {
  active: boolean;
};

type SubheaderTabLinkProps = ComponentProps<typeof Link> & {
  active: boolean;
  className?: string;
};

type TabEntry = {
  node: HTMLElement;
  active: boolean;
};

type IndicatorRect = {
  left: number;
  width: number;
  visible: boolean;
};

/** Tab height: `md` is the nav subheader's 32px, `sm` the 28px inline size. */
export type SubheaderTabSize = "md" | "sm";

const TAB_HEIGHT_CLASS: Record<SubheaderTabSize, string> = {
  md: "h-8",
  sm: "h-7",
};

type SubheaderTabListContextValue = {
  heightClass: string;
  radiusClass: string;
  weightClass: string;
  registerTab: (id: string, node: HTMLElement | null, active: boolean) => void;
  setHoveredTabId: (id: string | null) => void;
};

const SubheaderTabListContext =
  createContext<SubheaderTabListContextValue | null>(null);

export const SUBHEADER_TAB_ICON_CLASS = "size-3.5 shrink-0 text-current";

const DEFAULT_RADIUS_CLASS = "rounded-[10px]";

/** Tab weight: `medium` is the nav subheader's compact title; `normal` the 450
 * description weight of the outline buttons an inline strip sits among. */
export type SubheaderTabWeight = "medium" | "normal";

const TAB_WEIGHT_CLASS: Record<SubheaderTabWeight, string> = {
  medium: "font-medium-v4",
  normal: "font-normal-v4",
};

const getSubheaderTabClassName = (
  active: boolean,
  sliding: boolean,
  className?: string,
  heightClass: string = TAB_HEIGHT_CLASS.md,
  radiusClass: string = DEFAULT_RADIUS_CLASS,
  weightClass: string = TAB_WEIGHT_CLASS.medium,
): string =>
  cn(
    "relative z-[1] flex items-center gap-1 whitespace-nowrap px-2.5 py-1.5",
    heightClass,
    radiusClass,
    weightClass,
    "font-sans-v4 text-s-v4 leading-m-v4 tracking-m-v4 transition-colors",
    "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-brand-border",
    sliding
      ? active
        ? "text-text-primary"
        : "text-text-secondary hover:text-text-primary"
      : active
        ? "bg-bg-tertiary text-text-primary"
        : "text-text-secondary hover:bg-state-hover hover:text-text-primary",
    className,
  );

const SubheaderTabIndicator: FC<{ active: boolean }> = ({ active }) => (
  <span
    className={cn(
      "pointer-events-none absolute inset-x-2 bottom-0 h-px rounded-full transition-colors",
      active ? "bg-text-primary" : "bg-transparent",
    )}
  />
);

function useSubheaderTabRegistration(active: boolean) {
  const ctx = useContext(SubheaderTabListContext);
  const id = useId();
  const nodeRef = useRef<HTMLElement | null>(null);
  const activeRef = useRef(active);
  const ctxRef = useRef(ctx);

  activeRef.current = active;
  ctxRef.current = ctx;

  const setRef = useCallback(
    (node: HTMLElement | null) => {
      nodeRef.current = node;
      ctxRef.current?.registerTab(id, node, activeRef.current);
    },
    [id],
  );

  useLayoutEffect(() => {
    if (!nodeRef.current) return;
    ctxRef.current?.registerTab(id, nodeRef.current, active);
  }, [active, id]);

  useEffect(
    () => () => {
      ctxRef.current?.registerTab(id, null, false);
    },
    [id],
  );

  const onMouseEnter = useCallback(() => {
    ctxRef.current?.setHoveredTabId(id);
  }, [id]);

  return {
    sliding: ctx != null,
    heightClass: ctx?.heightClass ?? TAB_HEIGHT_CLASS.md,
    radiusClass: ctx?.radiusClass ?? DEFAULT_RADIUS_CLASS,
    weightClass: ctx?.weightClass ?? TAB_WEIGHT_CLASS.medium,
    setRef,
    onMouseEnter: ctx ? onMouseEnter : undefined,
  };
}

type SubheaderTabListProps = ComponentPropsWithoutRef<"div"> & {
  children: ReactNode;
  /** The active-tab underline. Off for uses outside the nav subheader, where
      the hover pill alone marks the selection. */
  underline?: boolean;
  size?: SubheaderTabSize;
  /** Fully round pills, for a strip that sits among round buttons. */
  shape?: "rounded" | "pill";
  weight?: SubheaderTabWeight;
};

export const SubheaderTabList = forwardRef<
  HTMLDivElement,
  SubheaderTabListProps
>(function SubheaderTabList(
  {
    children,
    className,
    onMouseLeave,
    role = "tablist",
    underline: showUnderline = true,
    size = "md",
    shape = "rounded",
    weight = "medium",
    ...props
  },
  forwardedRef,
) {
  const listRef = useRef<HTMLDivElement | null>(null);
  const tabsRef = useRef(new Map<string, TabEntry>());
  const hoveredTabIdRef = useRef<string | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const measureIndicatorsRef = useRef<() => void>(() => {});
  const [pill, setPill] = useState<IndicatorRect>({
    left: 0,
    width: 0,
    visible: false,
  });
  const [underline, setUnderline] = useState<IndicatorRect>({
    left: 0,
    width: 0,
    visible: false,
  });

  const setListRef = useCallback(
    (node: HTMLDivElement | null) => {
      const previous = listRef.current;
      listRef.current = node;

      if (previous && previous !== node) {
        resizeObserverRef.current?.unobserve(previous);
      }
      if (node) {
        resizeObserverRef.current?.observe(node);
      }

      if (typeof forwardedRef === "function") {
        forwardedRef(node);
      } else if (forwardedRef) {
        forwardedRef.current = node;
      }

      measureIndicatorsRef.current();
    },
    [forwardedRef],
  );

  const measureIndicators = useCallback(() => {
    const list = listRef.current;
    if (!list) return;

    const entries = Array.from(tabsRef.current.entries());
    const activeEntry = entries.find(([, entry]) => entry.active);
    const pillTargetId = hoveredTabIdRef.current ?? activeEntry?.[0] ?? null;
    const pillTarget = pillTargetId
      ? tabsRef.current.get(pillTargetId)
      : undefined;
    const listRect = list.getBoundingClientRect();

    if (!pillTarget) {
      setPill((prev) => (prev.visible ? { ...prev, visible: false } : prev));
    } else {
      const tabRect = pillTarget.node.getBoundingClientRect();
      const nextPill = {
        left: Math.round(tabRect.left - listRect.left + list.scrollLeft),
        width: Math.round(tabRect.width),
        visible: true,
      };
      setPill((prev) =>
        prev.left === nextPill.left &&
        prev.width === nextPill.width &&
        prev.visible === nextPill.visible
          ? prev
          : nextPill,
      );
    }

    if (!activeEntry) {
      setUnderline((prev) =>
        prev.visible ? { ...prev, visible: false } : prev,
      );
      return;
    }

    const tabRect = activeEntry[1].node.getBoundingClientRect();
    const nextUnderline = {
      // Match inset-x-2; vertical position is the list bottom (divider).
      left: Math.round(tabRect.left - listRect.left + list.scrollLeft + 8),
      width: Math.max(0, Math.round(tabRect.width - 16)),
      visible: true,
    };
    setUnderline((prev) =>
      prev.left === nextUnderline.left &&
      prev.width === nextUnderline.width &&
      prev.visible === nextUnderline.visible
        ? prev
        : nextUnderline,
    );
  }, []);

  measureIndicatorsRef.current = measureIndicators;

  const registerTab = useCallback(
    (id: string, node: HTMLElement | null, active: boolean) => {
      const previous = tabsRef.current.get(id);

      if (!node) {
        if (!previous) return;
        resizeObserverRef.current?.unobserve(previous.node);
        tabsRef.current.delete(id);
        measureIndicators();
        return;
      }

      if (previous?.node === node && previous.active === active) return;

      if (previous && previous.node !== node) {
        resizeObserverRef.current?.unobserve(previous.node);
      }
      if (!previous || previous.node !== node) {
        resizeObserverRef.current?.observe(node);
      }

      tabsRef.current.set(id, { node, active });
      measureIndicators();
    },
    [measureIndicators],
  );

  const setHoveredTabId = useCallback(
    (id: string | null) => {
      if (hoveredTabIdRef.current === id) return;
      hoveredTabIdRef.current = id;
      measureIndicators();
    },
    [measureIndicators],
  );

  useEffect(() => {
    const observer = new ResizeObserver(() => {
      measureIndicatorsRef.current();
    });
    resizeObserverRef.current = observer;

    const list = listRef.current;
    if (list) observer.observe(list);
    tabsRef.current.forEach((entry) => observer.observe(entry.node));

    const onScroll = () => measureIndicatorsRef.current();
    list?.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      observer.disconnect();
      resizeObserverRef.current = null;
      list?.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  const heightClass = TAB_HEIGHT_CLASS[size];
  const radiusClass = shape === "pill" ? "rounded-full" : DEFAULT_RADIUS_CLASS;
  const weightClass = TAB_WEIGHT_CLASS[weight];
  const contextValue = useMemo(
    () => ({
      heightClass,
      radiusClass,
      weightClass,
      registerTab,
      setHoveredTabId,
    }),
    [heightClass, radiusClass, weightClass, registerTab, setHoveredTabId],
  );

  return (
    <SubheaderTabListContext.Provider value={contextValue}>
      <div
        ref={setListRef}
        role={role}
        className={cn("relative h-full min-h-12 self-stretch", className)}
        onMouseLeave={(event) => {
          setHoveredTabId(null);
          onMouseLeave?.(event);
        }}
        {...props}
      >
        <div
          aria-hidden
          className={cn(
            "pointer-events-none absolute top-1/2 z-0 bg-bg-tertiary",
            heightClass,
            radiusClass,
            "transition-[transform,width,opacity] duration-200 ease-in-out",
            "motion-reduce:transition-none",
            pill.visible ? "opacity-100" : "opacity-0",
          )}
          style={{
            width: pill.width,
            transform: `translateX(${pill.left}px) translateY(-50%)`,
          }}
        />
        {showUnderline && (
          <div
            aria-hidden
            className={cn(
              "pointer-events-none absolute bottom-0 z-0 h-px rounded-full bg-text-primary",
              "transition-[transform,width,opacity] duration-200 ease-in-out",
              "motion-reduce:transition-none",
              underline.visible ? "opacity-100" : "opacity-0",
            )}
            style={{
              width: underline.width,
              transform: `translateX(${underline.left}px)`,
            }}
          />
        )}
        {children}
      </div>
    </SubheaderTabListContext.Provider>
  );
});

export const SubheaderTabButton: FC<SubheaderTabButtonProps> = ({
  active,
  children,
  className,
  type,
  onMouseEnter,
  ...props
}) => {
  const {
    sliding,
    heightClass,
    radiusClass,
    weightClass,
    setRef,
    onMouseEnter: onTabMouseEnter,
  } = useSubheaderTabRegistration(active);

  return (
    <button
      ref={setRef}
      type={type ?? "button"}
      role="tab"
      aria-selected={active}
      className={getSubheaderTabClassName(
        active,
        sliding,
        className,
        heightClass,
        radiusClass,
        weightClass,
      )}
      onMouseEnter={(event) => {
        onTabMouseEnter?.();
        onMouseEnter?.(event);
      }}
      {...props}
    >
      {children}
      {!sliding && <SubheaderTabIndicator active={active} />}
    </button>
  );
};

export const SubheaderTabLink: FC<SubheaderTabLinkProps> = ({
  active,
  children,
  className,
  onMouseEnter,
  ...props
}) => {
  const {
    sliding,
    heightClass,
    radiusClass,
    weightClass,
    setRef,
    onMouseEnter: onTabMouseEnter,
  } = useSubheaderTabRegistration(active);

  return (
    <span
      ref={setRef}
      className="inline-flex"
      onMouseEnter={() => {
        onTabMouseEnter?.();
      }}
    >
      <Link
        role="tab"
        aria-selected={active}
        className={getSubheaderTabClassName(
          active,
          sliding,
          className,
          heightClass,
          radiusClass,
          weightClass,
        )}
        onMouseEnter={onMouseEnter}
        {...props}
      >
        {children}
        {!sliding && <SubheaderTabIndicator active={active} />}
      </Link>
    </span>
  );
};

const SubheaderTabStrip: FC<Props> = ({
  tabs,
  value,
  onValueChange,
  ariaLabel = "Sub navigation",
  className,
}) => {
  if (tabs.length === 0) return null;

  return (
    <SubheaderTabList
      aria-label={ariaLabel}
      data-testid="subheader-tabs"
      className={cn(
        "order-first flex min-h-0 shrink-0 flex-nowrap items-center gap-1",
        className,
      )}
    >
      {tabs.map((tab) => {
        const isActive = value === tab.value;
        return (
          <SubheaderTabButton
            key={tab.value}
            active={isActive}
            data-testid={tab.testId ?? `tab-${tab.value}`}
            onClick={() => onValueChange(tab.value)}
          >
            {tab.label}
          </SubheaderTabButton>
        );
      })}
    </SubheaderTabList>
  );
};

export default SubheaderTabStrip;
